/**
 * DV-003: Telemetry Hook
 * Performance timing and logging utilities for demo viewer
 */

import { useCallback, useRef } from 'react';
import type { LogEntry, LogLevel } from './LogsPanel';

export interface TelemetryMark {
  name: string;
  timestamp: number;
  metadata?: Record<string, any>;
}

export interface TelemetryMeasure {
  name: string;
  startTime: number;
  endTime: number;
  duration: number;
  metadata?: Record<string, any>;
}

export interface UseTelemetryProps {
  onLog?: (entry: LogEntry) => void;
}

export function useTelemetry({ onLog }: UseTelemetryProps = {}) {
  const marksRef = useRef<Map<string, TelemetryMark>>(new Map());

  // Create a performance mark
  const mark = useCallback((name: string, metadata?: Record<string, any>) => {
    const timestamp = performance.now();
    const mark: TelemetryMark = {
      name,
      timestamp,
      metadata,
    };

    marksRef.current.set(name, mark);

    // Emit log entry for the mark
    if (onLog) {
      const logEntry: LogEntry = {
        id: `telemetry-mark-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
        ts: new Date().toISOString(),
        level: 'info' as LogLevel,
        text: `Mark: ${name}`,
        meta: {
          type: 'telemetry_mark',
          name,
          timestamp,
          ...metadata,
        },
      };
      onLog(logEntry);
    }

    return mark;
  }, [onLog]);

  // Create a performance measure between two marks or timestamps
  const measure = useCallback((
    name: string,
    startMark?: string | number,
    endMark?: string | number,
    metadata?: Record<string, any>
  ) => {
    const endTime = typeof endMark === 'number' ? endMark :
                   typeof endMark === 'string' ? marksRef.current.get(endMark)?.timestamp :
                   performance.now();

    const startTime = typeof startMark === 'number' ? startMark :
                     typeof startMark === 'string' ? marksRef.current.get(startMark)?.timestamp :
                     0;

    if (endTime === undefined || startTime === undefined) {
      console.warn(`useTelemetry: Could not find marks for measure "${name}"`);
      return null;
    }

    const duration = endTime - startTime;
    const measure: TelemetryMeasure = {
      name,
      startTime,
      endTime,
      duration,
      metadata,
    };

    // Emit log entry for the measure
    if (onLog) {
      const logEntry: LogEntry = {
        id: `telemetry-measure-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
        ts: new Date().toISOString(),
        level: 'info' as LogLevel,
        text: `Measure: ${name} (${duration.toFixed(2)}ms)`,
        meta: {
          type: 'telemetry_measure',
          name,
          duration,
          startTime,
          endTime,
          ...metadata,
        },
      };
      onLog(logEntry);
    }

    return measure;
  }, [onLog]);

  // Simple timing wrapper for functions
  const time = useCallback(async <T>(
    name: string,
    fn: () => T | Promise<T>,
    metadata?: Record<string, any>
  ): Promise<T> => {
    const startTime = performance.now();
    mark(`${name}_start`, metadata);

    try {
      const result = await fn();
      const endTime = performance.now();
      measure(name, startTime, endTime, { ...metadata, status: 'success' });
      return result;
    } catch (error) {
      const endTime = performance.now();
      measure(name, startTime, endTime, {
        ...metadata,
        status: 'error',
        error: error instanceof Error ? error.message : String(error)
      });
      throw error;
    }
  }, [mark, measure]);

  // Clear all stored marks (useful for cleanup)
  const clearMarks = useCallback(() => {
    marksRef.current.clear();
  }, []);

  // Get all current marks (for debugging)
  const getMarks = useCallback(() => {
    return Array.from(marksRef.current.values());
  }, []);

  return {
    mark,
    measure,
    time,
    clearMarks,
    getMarks,
  };
}